/**
 * Combines an array of class names into a single string, skipping falsy values.
 * @param {Array<string>} classes - An array of class names.
 * @returns {string} A string of space-separated class names.
 */
export const collateClasses = (classes=[]) => classes.filter(Boolean).join(" ")
// allows empty, blank or otherwise "falsey" values to be skipped

/**
 * Converts an object into a dataset-compatible format with attributes prefixed by 'data-'.
 * @param {Object} props - Object with key-value pairs to be converted.
 * @returns {Object} A new object with keys transformed into data-* attributes.
 */
export const collateDatasetProps = (props= {}) => {
    return Object.fromEntries(
        Object.entries(props).map(([key, value]) => [
            `data-${key.replace(/[A-Z]/g, m => `-${m.toLowerCase()}`)}`,
            value
        ])
    )
}


/**
 * Checks if an object, array, or value is empty.
 * @param {*} o - The value to check.
 * @returns {boolean} True if the value is null, undefined, an empty object, or an empty array.
 */
export const isEmpty = (o) => {
    // // return true if null or undefined, empty object (no keys) or empty array
    // if (o === null || o === undefined) return true
    // if (typeof o === 'object' && Object.keys(o).length === 0) return true
    // if (Array.isArray(o) && o.length === 0) return true
    //
    // return false

    // return true if null or undefined, empty object (no keys) or empty array
    return (o === null || o === undefined)
        || (typeof o === 'object' && Object.keys(o).length === 0)
        || (Array.isArray(o) && o.length === 0)

}

/**
 * Determines if an HTTP status code represents a successful response.
 * @param {number} statusCode - The HTTP status code to evaluate.
 * @returns {boolean} True if the status code is in the 200-399 range, false otherwise.
 */
export const isLinkStatusGood = (statusCode) => {
    return statusCode >= 200 && statusCode < 400
}


export const isLinkStatusBad = (statusCode) => {
    return statusCode < 200 || statusCode >= 400
}


export const normalizeUrlArray = (urlArray=[]) => {
    // transform urlArray, which is an array of url objects wrapped in a "data" property,
    // returns array of bare url objects extracted from "data" wrapper
    if (!urlArray || !urlArray.length) return [];
    return urlArray.map( uData => uData.data )
}
/**
 * Formats a number with thousands separators according to the specified locale.
 * @param {number} number - The number to format.
 * @param {string} [locale="en-US"] - Optional locale for formatting. Defaults to "en-US".
 * @returns {string} The formatted number.
 */
export const formatNumberWithLocale = (number, locale = "en-US") => {
    return number.toLocaleString(locale);
};
/*

onComplete: function to call when the copy is completed. If not provided, does an alert.
 */
export const copyToClipboard = (copyText, label="Data", onComplete) => {

    navigator.clipboard.writeText(copyText)
        .then(() => {
            console.log(`${label} copied to clipboard.`);
            if (onComplete) {
                onComplete(`${label} copied to Clipboard`)
            } else {
                iareAlert(`${label} Copied to clipboard.`);
            }
        })
        .catch((error) => {
            console.error(`Failed to copy ${label} to clipboard.`, error);
            alert(`Failed to copy ${label} to clipboard: ${error}`);
        });
}

// returns hexadecimal color for interpolated gradient between start and end colors
//
// startColor and endColor are rgb triplet integer arrays: [ <r>, <g>, <b> ]
// steps is how many steps in between first and last color
// index is which of those step colors to return, 0 based
export const getColorFromIndex = (index, startColor, endColor, steps) => {
    function rgbToHex(r, g, b) {
        const hexR = r.toString(16).padStart(2, "0"); // Convert to hex and pad with 0 if needed
        const hexG = g.toString(16).padStart(2, "0");
        const hexB = b.toString(16).padStart(2, "0");
        return `#${hexR}${hexG}${hexB}`; // Return the hexadecimal color string
    }
    if (steps <= 1) {
        return rgbToHex(endColor[0],endColor[1],endColor[2]);
    }
    const r = Math.floor(startColor[0] + (index / (steps -1) * (endColor[0] - startColor[0]) ));
    const g = Math.floor(startColor[1] + (index / (steps -1) * (endColor[1] - startColor[1]) ));
    const b = Math.floor(startColor[2] + (index / (steps -1) * (endColor[2] - startColor[2]) ));
    return rgbToHex(r,g,b);
}

// startColor and endColor are #AABBCC hex format
// returns array of colors that are a fade from start color to end color
// Example usage:
//   const startColor = '#ff0000'; // Red
//   const endColor = '#00ff00'; // Green
//   const steps = 10; // Number of steps
//
//   const colorArray = generateColorFade(startColor, endColor, steps);
//   console.log(colorArray);
export const generateColorFade = (startColor, endColor, steps) => {
    if (steps < 2) return [ startColor ]

    // Convert start and end colors to RGB format
    const startRGB = hexToRgb(startColor);
    const endRGB = hexToRgb(endColor);

    // Calculate the step increments for each color channel
    const stepIncrement = {
        r: (endRGB.r - startRGB.r) / (steps - 1),
        g: (endRGB.g - startRGB.g) / (steps - 1),
        b: (endRGB.b - startRGB.b) / (steps - 1),
    };

    // Generate the array of color specifications
    const colorArray = Array.from({ length: steps }, (_, index) => {
        const currentColor = {
            r: Math.round(startRGB.r + stepIncrement.r * index),
            g: Math.round(startRGB.g + stepIncrement.g * index),
            b: Math.round(startRGB.b + stepIncrement.b * index),
        };
        return rgbToHex(currentColor);
    });

    return colorArray;
}

// Helper function to convert hex color to RGB
function hexToRgb(hex) {
    const bigint = parseInt(hex.slice(1), 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return { r: r, g: g, b: b };
}

// Helper function to convert RGB color to hex
function rgbToHex(rgb) {
    return (
        '#' +
        ((1 << 24) + (rgb.r << 16) + (rgb.g << 8) + rgb.b)
            .toString(16)
            .slice(1)
    );
}

export const convertToCSV = (json) => {
    const rows = json.map((row) => {

        const myRowItems = row.map( (item) => {
            // from: https://stackoverflow.com/questions/46637955/write-a-string-containing-commas-and-double-quotes-to-csv
            // We remove blanks and check if the item contains other whitespace,`,` or `"`.
            // In that case, we need to quote the item.

            if (typeof item === 'string') {
                if (item.replace(/ /g, '').match(/[\s,"]/)) {
                    return '"' + item.replace(/"/g, '""') + '"';
                }
                return item
            } else {
                return item
            }
        })
        return myRowItems.join(',');
    });
    return rows.join('\n');
}


export const areObjectsEqual = (objA, objB) => {
    // Get the keys of the objects
    const keysA = Object.keys(objA);
    const keysB = Object.keys(objB);

    // Check if the number of keys is the same
    if (keysA.length !== keysB.length) {
        return false;
    }

    // Iterate through keys and compare values
    for (let key of keysA) {
        // If the values of the current key are not the same, objects are not equal
        if (objA[key] !== objB[key]) {
            return false;
        }
    }

    // If all values are equal, objects are equal
    return true;
}

export const getLinkStatus = (statusCode) => {
    return (statusCode === undefined)
        ? 'none'
        : (statusCode >= 200 && statusCode < 400)
            ? 'good'
            : 'bad'
}

export const debounce = (fn, delay) => {
    let timeoutId;
    return (...args) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => fn(...args), delay);
    };
}


// assumes score is between 0 and 1
// if not defined, return -1, a negative value indicating it is not valid
export const getNormalizedScore = (rawScore) => {
    if (rawScore === undefined || rawScore === "") {
        return -1;
    }
    return Number(rawScore).toFixed(2)
}

// like getNormalizedScore but no decimal fix
export const getNormalizedCount = (count) => {
    if (count === undefined || count === "") {
        return -1;
    }
    return Number(count)
}


// returns -1 if not provided
export const getPrettyCount = (rawScore) => {
    if (rawScore === undefined || rawScore === "") {
        return -1;
    }
    return trimifyNumber(Number(rawScore))
}


export const trimifyNumber = (n) => {
    return n >= 1000000 
        ? `${(n / 1000000).toFixed(1)}M` 
        : (n > 1000
                ? `${(n / 1000).toFixed(1)}K`
                : `${n}`
        )
}


export const iareAlert = (s, contextId) => {
    // if contextId specified, use that element to fade in alert text and then fadeout
    // otherwise just use alert

    const timeWait = 2500

    const targetElement = contextId
        ? document.querySelector(`#${contextId}`)
        : null;

    if (targetElement) {
        targetElement.innerText = s;
        targetElement.style.transition = "opacity 0.5s ease-in";
        targetElement.style.opacity = "1";

        setTimeout(() => {
            targetElement.style.transition = "opacity 0.5s ease-out";
            targetElement.style.opacity = "0";

            setTimeout(() => {
                targetElement.innerText = "";
            }, 500);
        }, timeWait);
    } else {
        alert(s);
    }
}


/**
 * Logs a debug message to a specific DOM element or a default debug box.
 * @param {string} msg - The debug message to display.
 * @param {string} [contextId] - The ID of the target DOM element. If not provided,
 *                              defaults to an element with the class 'draggableDebugBox'.
 */
export const iareDebug = (msg, contextId) => {

    const targetElement = contextId
        ? document.querySelector(`#${contextId}`)
        : document.querySelector(`.draggableDebugBox`);

    if (targetElement) {
        targetElement.innerText = msg;
    } else {
        console.warn(`Debug target element (#${contextId}) not found,
        and there is no .draggableDebugBox element to log debug messages to.`)
    }
}


